/**
 * 암호화 백업 파일 (SYNC_SECURITY §12, 계획서 §17-5).
 *
 * 파일 = 평문 헤더(형식, vault 헤더[포장된 키만], 스키마 버전, 생성 시각) + 암호화 본문.
 * 본문 = 모든 암호화 행(entities, history, blobs) + 저장소별 SHA-256 manifest + prompt 버전.
 * 행 자체가 이미 암호문이고, 본문 전체를 한 번 더 암호화해 개수·시각 같은 메타데이터도 숨긴다.
 *
 * 복원은 "검증 완료 → 현재 상태 스냅샷 → 단일 트랜잭션 교체" 순서. 검증 단계에서 실패하면 DB를 건드리지 않는다.
 */
import { SCHEMA_VERSION } from '@/domain/schema/v1';
import {
  envelopeFromJson,
  envelopeToJson,
  open,
  openJson,
  sealJson,
  type DataKey,
  type EnvelopeJson,
} from './crypto/envelope';
import { sha256Hex, utf8 } from './crypto/encoding';
import { isVaultHeader, unlockWithPassphrase, type VaultHeader } from './crypto/vault';
import { META, type BlobChunk, type CounselDb, type StoredRecord } from './db/schema';
import { IntegrityError } from './errors';
import { recordAad } from './repo/repository';
import { createSnapshot, readAllRows, replaceAllRows, type SnapshotPayload } from './snapshots';

export const BACKUP_FORMAT = 'counselai-backup';
export const BACKUP_EXTENSION = '.counselai-backup';

type RowJson = Omit<StoredRecord, 'env'> & { env: EnvelopeJson };
type BlobJson = Omit<BlobChunk, 'env'> & { env: EnvelopeJson };

type BackupBody = {
  dataSchemaVersion: number;
  entities: RowJson[];
  history: RowJson[];
  blobs: BlobJson[];
  manifest: {
    entities: string;
    history: string;
    blobs: string;
    counts: { entities: number; history: number; blobs: number };
  };
  promptVersions?: Record<string, string>;
};

export type BackupFile = {
  format: typeof BACKUP_FORMAT;
  formatVersion: 1;
  createdAt: string;
  dataSchemaVersion: number;
  vault: VaultHeader;
  body: EnvelopeJson;
};

const bodyAad = (vaultId: string, createdAt: string) => `counselai|backup|${vaultId}|${createdAt}`;
const rowToJson = (r: StoredRecord): RowJson => ({ ...r, env: envelopeToJson(r.env) });
const rowFromJson = (r: RowJson): StoredRecord => ({ ...r, env: envelopeFromJson(r.env) });
const blobToJson = (b: BlobChunk): BlobJson => ({ ...b, env: envelopeToJson(b.env) });
const blobFromJson = (b: BlobJson): BlobChunk => ({ ...b, env: envelopeFromJson(b.env) });

async function manifestOf(entities: unknown[], history: unknown[], blobs: unknown[]) {
  const h = (x: unknown) => sha256Hex(utf8.encode(JSON.stringify(x)));
  return {
    entities: await h(entities),
    history: await h(history),
    blobs: await h(blobs),
    counts: { entities: entities.length, history: history.length, blobs: blobs.length },
  };
}

export async function exportBackup(
  db: CounselDb,
  dataKey: DataKey,
  opts: { now?: Date; promptVersions?: Record<string, string> } = {},
): Promise<{ fileName: string; text: string }> {
  const rows = await readAllRows(db);
  if (!rows) throw new Error('설정된 vault가 없습니다');
  const createdAt = (opts.now ?? new Date()).toISOString();
  const entities = rows.entities.map(rowToJson);
  const history = rows.history.map(rowToJson);
  const blobs = (rows.blobs ?? []).map(blobToJson);
  const body: BackupBody = {
    dataSchemaVersion: rows.dataSchemaVersion,
    entities,
    history,
    blobs,
    manifest: await manifestOf(entities, history, blobs),
    ...(opts.promptVersions ? { promptVersions: opts.promptVersions } : {}),
  };
  const file: BackupFile = {
    format: BACKUP_FORMAT,
    formatVersion: 1,
    createdAt,
    dataSchemaVersion: rows.dataSchemaVersion,
    vault: rows.vault,
    body: envelopeToJson(await sealJson(dataKey, body, bodyAad(rows.vault.vaultId, createdAt))),
  };
  await db.meta.put({ key: META.lastBackupAt, value: createdAt });
  const stamp = createdAt.replace(/[:.]/g, '-').slice(0, 19);
  return { fileName: `counselai-${stamp}${BACKUP_EXTENSION}`, text: JSON.stringify(file) };
}

export type BackupVerification = { file: BackupFile; dataKey: DataKey; payload: SnapshotPayload };

/**
 * 백업 파일 검증 (DB를 건드리지 않음): 형식 → passphrase → 본문 복호화 → manifest → 모든 행 복호화.
 */
export async function verifyBackup(text: string, passphrase: string): Promise<BackupVerification> {
  let file: BackupFile;
  try {
    file = JSON.parse(text) as BackupFile;
  } catch {
    throw new IntegrityError('백업 파일 형식이 올바르지 않습니다');
  }
  if (file?.format !== BACKUP_FORMAT || file.formatVersion !== 1 || !isVaultHeader(file.vault)) {
    throw new IntegrityError('CounselAI 백업 파일이 아닙니다');
  }
  if (file.dataSchemaVersion > SCHEMA_VERSION) {
    throw new IntegrityError('이 앱보다 새로운 버전에서 만든 백업입니다. 앱을 업데이트한 뒤 복원하세요.');
  }
  const dataKey = await unlockWithPassphrase(file.vault, passphrase); // WrongSecretError
  const body = await openJson<BackupBody>(
    dataKey,
    envelopeFromJson(file.body),
    bodyAad(file.vault.vaultId, file.createdAt),
  );

  const m = await manifestOf(body.entities, body.history, body.blobs);
  if (JSON.stringify(m) !== JSON.stringify(body.manifest)) throw new IntegrityError('백업 manifest 불일치');

  const entities = body.entities.map(rowFromJson);
  const history = body.history.map(rowFromJson);
  const blobs = body.blobs.map(blobFromJson);

  // 모든 행이 이 키로 복호화되는지 확인 (손상 행이 하나라도 있으면 복원하지 않는다)
  for (const r of [...entities, ...history]) await open(dataKey, r.env, recordAad(file.vault.vaultId, r));
  for (const b of blobs)
    await open(dataKey, b.env, `counselai|blob|${file.vault.vaultId}|${b.blobId}|${b.chunk}|${b.total}`);

  return {
    file,
    dataKey,
    payload: { vault: file.vault, dataSchemaVersion: body.dataSchemaVersion, entities, history, blobs },
  };
}

/**
 * 복원. 검증을 모두 통과한 뒤에만 현재 상태를 스냅샷으로 남기고 교체한다.
 * 복원 후에는 백업 파일의 passphrase로 잠금 해제한다.
 */
export async function restoreBackup(
  db: CounselDb,
  text: string,
  passphrase: string,
): Promise<{ dataKey: DataKey; header: VaultHeader }> {
  const v = await verifyBackup(text, passphrase);
  await createSnapshot(db, 'pre-restore');
  await replaceAllRows(db, v.payload);
  return { dataKey: v.dataKey, header: v.file.vault };
}
